/**
 * AdminFeedbackDashboard Component
 * Q AI Feedback & Training Data Management
 *
 * Features:
 * - View all user feedback (thumbs up/down)
 * - Export training data in JSONL format
 * - Sync feedback to prompts for reinforcement learning
 * - Filter by rating, date range, user
 */

import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    Button,
    TextField,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    LinearProgress,
    FormControl,
    InputLabel,
    Select,
    Avatar,
    Pagination,
    CircularProgress,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Alert,
    Tabs,
    Tab
} from '@mui/material';
import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
    MoreVert as MoreVertIcon,
    ThumbUp as ThumbUpIcon,
    ThumbDown as ThumbDownIcon,
    Download as DownloadIcon,
    Sync as SyncIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    Assessment as AssessmentIcon,
    Timeline as TimelineIcon,
    Psychology as PsychologyIcon,
    CheckCircle as CheckCircleIcon,
    History as HistoryIcon,
    FilterList as FilterListIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
    getFeedbackData,
    getFeedbackStats,
    exportTrainingData,
    syncFeedbackToPrompts,
    deleteFeedback,
    getTrainingSyncHistory,
    getIssueTypes,
    resolveFeedback
} from '../../api/admin_api';

// Embedded theme (matching other admin components)
const theme = {
    colors: {
        bg: { primary: '#191b20', secondary: '#292b2f', tertiary: '#333539' },
        text: { primary: 'rgba(255,255,255,1)', secondary: 'rgba(255,255,255,0.9)', tertiary: 'rgba(255,255,255,0.7)', quaternary: 'rgba(255,255,255,0.5)' },
        accent: { primary: '#7c3aed', secondary: '#5b21b6', tertiary: '#a78bfa' },
        border: { primary: 'rgba(255,255,255,0.1)', secondary: 'rgba(255,255,255,0.05)' },
        status: { success: '#22c55e', error: '#ef4444', warning: '#f59e0b', info: '#3b82f6' }
    },
    gradients: { primaryAccent: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)' },
    borderRadius: { sm: '8px', md: '12px' }
};

const AdminFeedbackDashboard = forwardRef((props, ref) => {
    // State management
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [ratingFilter, setRatingFilter] = useState('all');
    const [issueTypeFilter, setIssueTypeFilter] = useState('all');
    const [resolvedFilter, setResolvedFilter] = useState('all');
    const [feedback, setFeedback] = useState([]);
    const [issueTypes, setIssueTypes] = useState([]);
    const [page, setPage] = useState(1);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [stats, setStats] = useState({
        totalFeedback: 0,
        goodFeedback: 0,
        badFeedback: 0,
        neutralFeedback: 0,
        unresolvedCount: 0,
        resolvedCount: 0,
        issuesByType: [],
        recentFeedback: []
    });

    // Modal states
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [syncModalOpen, setSyncModalOpen] = useState(false);
    const [syncHistory, setSyncHistory] = useState([]);
    const [syncing, setSyncing] = useState(false);
    const [exporting, setExporting] = useState(false);

    // Export options
    const [exportFormat, setExportFormat] = useState('jsonl');
    const [includeGood, setIncludeGood] = useState(true);
    const [includeBad, setIncludeBad] = useState(true);

    // Resolve modal
    const [resolveModalOpen, setResolveModalOpen] = useState(false);
    const [resolutionNotes, setResolutionNotes] = useState('');
    const [resolving, setResolving] = useState(false);

    const rowsPerPage = 10;

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
        refresh: fetchFeedback
    }), []);

    // Fetch feedback on mount
    useEffect(() => {
        fetchFeedback();
        fetchStats();
        fetchIssueTypes();
    }, []);

    const fetchFeedback = async () => {
        setLoading(true);
        try {
            const result = await getFeedbackData({ limit: 500 });
            if (result.success) {
                setFeedback(result.data || []);
            } else {
                toast.error(result.error || 'Failed to fetch feedback');
            }
        } catch (error) {
            console.error('Error fetching feedback:', error);
            toast.error('Failed to fetch feedback');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const result = await getFeedbackStats();
            if (result.success) {
                setStats(result.data);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const fetchIssueTypes = async () => {
        try {
            const result = await getIssueTypes();
            if (result.success && Array.isArray(result.data)) {
                setIssueTypes(result.data);
            } else {
                // Ensure issueTypes is always an array
                setIssueTypes([]);
            }
        } catch (error) {
            console.error('Error fetching issue types:', error);
            setIssueTypes([]);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await Promise.all([fetchFeedback(), fetchStats()]);
        setRefreshing(false);
        toast.success('Data refreshed');
    };

    const handleMenuOpen = (event, item) => {
        setAnchorEl(event.currentTarget);
        setSelectedFeedback(item);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleViewDetails = () => {
        handleMenuClose();
        setViewModalOpen(true);
    };

    const handleDeleteFeedback = async () => {
        if (!selectedFeedback) return;
        handleMenuClose();

        const result = await deleteFeedback(selectedFeedback.id);
        if (result.success) {
            toast.success('Feedback deleted');
            fetchFeedback();
            fetchStats();
        } else {
            toast.error(result.error || 'Failed to delete feedback');
        }
    };

    const handleOpenResolveModal = () => {
        handleMenuClose();
        setResolutionNotes('');
        setResolveModalOpen(true);
    };

    const handleResolveFeedback = async () => {
        if (!selectedFeedback) return;
        setResolving(true);

        const result = await resolveFeedback(selectedFeedback.id, resolutionNotes);
        if (result.success) {
            toast.success('Feedback marked as resolved');
            setResolveModalOpen(false);
            fetchFeedback();
            fetchStats();
        } else {
            toast.error(result.error || 'Failed to resolve feedback');
        }
        setResolving(false);
    };

    // Export training data
    const handleExport = async () => {
        setExporting(true);
        try {
            const result = await exportTrainingData({
                format: exportFormat,
                includeGood,
                includeBad
            });

            if (result.success) {
                if (result.data instanceof Blob) {
                    // Download file
                    const url = window.URL.createObjectURL(result.data);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = result.filename || 'qai_training_data.jsonl';
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                    toast.success(`Exported ${result.count || 'training'} data successfully`);
                } else {
                    toast.success(`Export prepared: ${result.count} records`);
                }
                setExportModalOpen(false);
            } else {
                toast.error(result.error || 'Export failed');
            }
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Export failed');
        } finally {
            setExporting(false);
        }
    };

    // Sync feedback to prompts
    const handleSync = async (previewOnly = false) => {
        setSyncing(true);
        try {
            const result = await syncFeedbackToPrompts(previewOnly);

            if (result.success) {
                const prompts = result.prompts || [];
                if (previewOnly) {
                    // Show preview info
                    const summary = prompts.length > 0
                        ? prompts.map(p => `${p.promptKey}: ${p.action}`).join('\n')
                        : 'No changes to sync';
                    toast.info(`Preview: ${result.message || summary}`, { autoClose: 5000 });
                } else {
                    // Actual sync
                    const summary = prompts.map(p => `${p.promptKey}: ${p.action}`).join(', ');
                    toast.success(result.message + (summary ? ` (${summary})` : ''));
                    fetchStats();
                    // Refresh sync history
                    const historyResult = await getTrainingSyncHistory(10);
                    if (historyResult.success) {
                        setSyncHistory(historyResult.data || []);
                    }
                    setSyncModalOpen(false);
                }
            } else {
                toast.error(result.error || 'Sync failed');
            }
        } catch (error) {
            console.error('Sync error:', error);
            toast.error('Sync failed');
        } finally {
            setSyncing(false);
        }
    };

    // Fetch sync history when opening sync modal
    const handleOpenSyncModal = async () => {
        setSyncModalOpen(true);
        try {
            const result = await getTrainingSyncHistory(10);
            if (result.success) {
                setSyncHistory(result.data || []);
            }
        } catch (error) {
            console.error('Error fetching sync history:', error);
        }
    };

    // Filter feedback
    const filteredFeedback = feedback.filter(item => {
        const matchesSearch = !searchQuery ||
            item.userMessage?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.qResponse?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.userId?.toString().includes(searchQuery);

        const matchesRating = ratingFilter === 'all' ||
            (ratingFilter === 'good' && item.rating === 'good') ||
            (ratingFilter === 'bad' && item.rating === 'bad') ||
            (ratingFilter === 'neutral' && item.rating === 'neutral');

        const matchesIssueType = issueTypeFilter === 'all' || item.issueType === issueTypeFilter;

        const matchesResolved = resolvedFilter === 'all' ||
            (resolvedFilter === 'resolved' && item.resolvedAt) ||
            (resolvedFilter === 'unresolved' && !item.resolvedAt);

        return matchesSearch && matchesRating && matchesIssueType && matchesResolved;
    });

    // Pagination
    const paginatedFeedback = filteredFeedback.slice((page - 1) * rowsPerPage, page * rowsPerPage);
    const totalPages = Math.ceil(filteredFeedback.length / rowsPerPage);

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const truncateText = (text, maxLength = 100) => {
        if (!text) return '-';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    const getRatingIcon = (rating) => {
        switch (rating) {
            case 'good':
                return <ThumbUpIcon sx={{ color: theme.colors.status.success, fontSize: 18 }} />;
            case 'bad':
                return <ThumbDownIcon sx={{ color: theme.colors.status.error, fontSize: 18 }} />;
            default:
                return null;
        }
    };

    const getRatingColor = (rating) => {
        switch (rating) {
            case 'good':
                return { bg: theme.colors.status.success + '20', text: theme.colors.status.success };
            case 'bad':
                return { bg: theme.colors.status.error + '20', text: theme.colors.status.error };
            default:
                return { bg: theme.colors.text.quaternary + '20', text: theme.colors.text.quaternary };
        }
    };

    return (
        <Box sx={{ p: 3, background: theme.colors.bg.primary, minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" sx={{ color: theme.colors.text.primary, fontWeight: 700 }}>
                        Q AI Feedback & Training
                    </Typography>
                    <Typography sx={{ color: theme.colors.text.quaternary, mt: 0.5 }}>
                        Manage user feedback and export training data for reinforcement learning
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={() => setExportModalOpen(true)}
                        sx={{
                            background: theme.colors.status.info,
                            '&:hover': { background: '#2563eb' }
                        }}
                    >
                        Export Data
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SyncIcon />}
                        onClick={handleOpenSyncModal}
                        sx={{
                            background: theme.gradients.primaryAccent,
                            '&:hover': { background: theme.colors.accent.secondary }
                        }}
                    >
                        Sync to Prompts
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
                        onClick={handleRefresh}
                        disabled={refreshing}
                        sx={{
                            borderColor: theme.colors.border.primary,
                            color: theme.colors.text.secondary,
                            '&:hover': { borderColor: theme.colors.accent.primary }
                        }}
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ background: theme.colors.status.info, width: 48, height: 48 }}>
                                    <AssessmentIcon />
                                </Avatar>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                                        Total Feedback
                                    </Typography>
                                    <Typography sx={{ color: theme.colors.text.primary, fontSize: '1.5rem', fontWeight: 700 }}>
                                        {stats.totalFeedback}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ background: theme.colors.status.success, width: 48, height: 48 }}>
                                    <ThumbUpIcon />
                                </Avatar>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                                        Positive (Good)
                                    </Typography>
                                    <Typography sx={{ color: theme.colors.text.primary, fontSize: '1.5rem', fontWeight: 700 }}>
                                        {stats.goodFeedback}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ background: theme.colors.status.error, width: 48, height: 48 }}>
                                    <ThumbDownIcon />
                                </Avatar>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                                        Negative (Bad)
                                    </Typography>
                                    <Typography sx={{ color: theme.colors.text.primary, fontSize: '1.5rem', fontWeight: 700 }}>
                                        {stats.badFeedback}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ background: theme.colors.status.warning, width: 48, height: 48 }}>
                                    <FilterListIcon />
                                </Avatar>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                                        Unresolved
                                    </Typography>
                                    <Typography sx={{ color: theme.colors.text.primary, fontSize: '1.5rem', fontWeight: 700 }}>
                                        {stats.unresolvedCount}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3, background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            placeholder="Search by message content..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: theme.colors.text.quaternary }} />
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    background: theme.colors.bg.tertiary,
                                    '& fieldset': { borderColor: theme.colors.border.primary }
                                },
                                '& .MuiInputBase-input': { color: theme.colors.text.primary }
                            }}
                        />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ color: theme.colors.text.quaternary }}>Rating</InputLabel>
                            <Select
                                value={ratingFilter}
                                label="Rating"
                                onChange={(e) => setRatingFilter(e.target.value)}
                                sx={{
                                    background: theme.colors.bg.tertiary,
                                    color: theme.colors.text.primary,
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.colors.border.primary }
                                }}
                            >
                                <MenuItem value="all">All Ratings</MenuItem>
                                <MenuItem value="good">Good</MenuItem>
                                <MenuItem value="bad">Bad</MenuItem>
                                <MenuItem value="neutral">Neutral</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ color: theme.colors.text.quaternary }}>Issue Type</InputLabel>
                            <Select
                                value={issueTypeFilter}
                                label="Issue Type"
                                onChange={(e) => setIssueTypeFilter(e.target.value)}
                                sx={{
                                    background: theme.colors.bg.tertiary,
                                    color: theme.colors.text.primary,
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.colors.border.primary }
                                }}
                            >
                                <MenuItem value="all">All Types</MenuItem>
                                {Array.isArray(issueTypes) && issueTypes.map((type) => (
                                    <MenuItem key={type.issueType} value={type.issueType}>
                                        {type.issueType?.replace(/_/g, ' ') || type.issueType}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ color: theme.colors.text.quaternary }}>Status</InputLabel>
                            <Select
                                value={resolvedFilter}
                                label="Status"
                                onChange={(e) => setResolvedFilter(e.target.value)}
                                sx={{
                                    background: theme.colors.bg.tertiary,
                                    color: theme.colors.text.primary,
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.colors.border.primary }
                                }}
                            >
                                <MenuItem value="all">All Status</MenuItem>
                                <MenuItem value="unresolved">Unresolved</MenuItem>
                                <MenuItem value="resolved">Resolved</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <Typography sx={{ color: theme.colors.text.tertiary, fontSize: '0.85rem' }}>
                            {filteredFeedback.length} of {feedback.length}
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            {/* Feedback Table */}
            <TableContainer component={Paper} sx={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                {loading && <LinearProgress />}
                <Table>
                    <TableHead>
                        <TableRow sx={{ background: theme.colors.bg.tertiary }}>
                            <TableCell sx={{ color: theme.colors.text.tertiary, fontWeight: 600 }}>Rating</TableCell>
                            <TableCell sx={{ color: theme.colors.text.tertiary, fontWeight: 600 }}>Issue Type</TableCell>
                            <TableCell sx={{ color: theme.colors.text.tertiary, fontWeight: 600 }}>User Message</TableCell>
                            <TableCell sx={{ color: theme.colors.text.tertiary, fontWeight: 600 }}>Q Response</TableCell>
                            <TableCell sx={{ color: theme.colors.text.tertiary, fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ color: theme.colors.text.tertiary, fontWeight: 600 }}>Date</TableCell>
                            <TableCell sx={{ color: theme.colors.text.tertiary, fontWeight: 600 }} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedFeedback.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                    <Typography sx={{ color: theme.colors.text.quaternary }}>
                                        {loading ? 'Loading...' : 'No feedback found'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedFeedback.map((item) => {
                                const ratingStyle = getRatingColor(item.rating);
                                const isResolved = !!item.resolvedAt;
                                return (
                                    <TableRow key={item.id} hover sx={{ '&:hover': { background: theme.colors.bg.tertiary } }}>
                                        <TableCell>
                                            <Chip
                                                icon={getRatingIcon(item.rating)}
                                                label={item.rating?.charAt(0).toUpperCase() + item.rating?.slice(1) || 'N/A'}
                                                size="small"
                                                sx={{
                                                    background: ratingStyle.bg,
                                                    color: ratingStyle.text,
                                                    fontWeight: 600
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ color: theme.colors.text.tertiary, fontSize: '0.8rem', textTransform: 'capitalize' }}>
                                                {item.issueType?.replace(/_/g, ' ') || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={item.userMessage || ''}>
                                                <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.85rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {truncateText(item.userMessage, 50)}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={item.qResponse || ''}>
                                                <Typography sx={{ color: theme.colors.text.tertiary, fontSize: '0.85rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {truncateText(item.qResponse, 60)}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={isResolved ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : null}
                                                label={isResolved ? 'Resolved' : 'Open'}
                                                size="small"
                                                sx={{
                                                    background: isResolved ? theme.colors.status.success + '20' : theme.colors.status.warning + '20',
                                                    color: isResolved ? theme.colors.status.success : theme.colors.status.warning,
                                                    fontSize: '0.75rem'
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ color: theme.colors.text.tertiary, fontSize: '0.8rem' }}>
                                                {formatDate(item.createdAt)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                onClick={(e) => handleMenuOpen(e, item)}
                                                sx={{ color: theme.colors.text.tertiary }}
                                            >
                                                <MoreVertIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(e, value) => setPage(value)}
                        sx={{
                            '& .MuiPaginationItem-root': {
                                color: theme.colors.text.secondary,
                                '&.Mui-selected': {
                                    background: theme.colors.accent.primary,
                                    color: theme.colors.text.primary
                                }
                            }
                        }}
                    />
                </Box>
            )}

            {/* Action Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        background: theme.colors.bg.secondary,
                        border: `1px solid ${theme.colors.border.primary}`
                    }
                }}
            >
                <MenuItem onClick={handleViewDetails} sx={{ color: theme.colors.text.primary }}>
                    <VisibilityIcon sx={{ mr: 1, color: theme.colors.status.info }} />
                    View Details
                </MenuItem>
                {selectedFeedback && !selectedFeedback.resolvedAt && (
                    <MenuItem onClick={handleOpenResolveModal} sx={{ color: theme.colors.text.primary }}>
                        <CheckCircleIcon sx={{ mr: 1, color: theme.colors.status.success }} />
                        Mark as Resolved
                    </MenuItem>
                )}
                <MenuItem onClick={handleDeleteFeedback} sx={{ color: theme.colors.text.primary }}>
                    <DeleteIcon sx={{ mr: 1, color: theme.colors.status.error }} />
                    Delete
                </MenuItem>
            </Menu>

            {/* View Details Modal */}
            <Dialog
                open={viewModalOpen}
                onClose={() => setViewModalOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        background: theme.colors.bg.secondary,
                        border: `1px solid ${theme.colors.border.primary}`
                    }
                }}
            >
                <DialogTitle sx={{ color: theme.colors.text.primary, borderBottom: `1px solid ${theme.colors.border.primary}` }}>
                    Feedback Details
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    {selectedFeedback && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem', mb: 0.5 }}>
                                        Rating
                                    </Typography>
                                    <Chip
                                        icon={getRatingIcon(selectedFeedback.rating)}
                                        label={selectedFeedback.rating?.charAt(0).toUpperCase() + selectedFeedback.rating?.slice(1)}
                                        sx={{
                                            background: getRatingColor(selectedFeedback.rating).bg,
                                            color: getRatingColor(selectedFeedback.rating).text,
                                            fontWeight: 600
                                        }}
                                    />
                                </Box>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem', mb: 0.5 }}>
                                        Issue Type
                                    </Typography>
                                    <Chip
                                        label={selectedFeedback.issueType?.replace(/_/g, ' ') || 'N/A'}
                                        sx={{
                                            background: theme.colors.bg.tertiary,
                                            color: theme.colors.text.secondary,
                                            textTransform: 'capitalize'
                                        }}
                                    />
                                </Box>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem', mb: 0.5 }}>
                                        Status
                                    </Typography>
                                    <Chip
                                        icon={selectedFeedback.resolvedAt ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : null}
                                        label={selectedFeedback.resolvedAt ? 'Resolved' : 'Open'}
                                        sx={{
                                            background: selectedFeedback.resolvedAt ? theme.colors.status.success + '20' : theme.colors.status.warning + '20',
                                            color: selectedFeedback.resolvedAt ? theme.colors.status.success : theme.colors.status.warning
                                        }}
                                    />
                                </Box>
                            </Box>

                            <Box>
                                <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem', mb: 0.5 }}>
                                    User Message
                                </Typography>
                                <Paper sx={{ p: 2, background: theme.colors.bg.tertiary, border: `1px solid ${theme.colors.border.primary}` }}>
                                    <Typography sx={{ color: theme.colors.text.secondary, whiteSpace: 'pre-wrap' }}>
                                        {selectedFeedback.userMessage || '-'}
                                    </Typography>
                                </Paper>
                            </Box>

                            <Box>
                                <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem', mb: 0.5 }}>
                                    Q Response
                                </Typography>
                                <Paper sx={{ p: 2, background: theme.colors.bg.tertiary, border: `1px solid ${theme.colors.border.primary}`, maxHeight: 300, overflow: 'auto' }}>
                                    <Typography sx={{ color: theme.colors.text.secondary, whiteSpace: 'pre-wrap' }}>
                                        {selectedFeedback.qResponse || '-'}
                                    </Typography>
                                </Paper>
                            </Box>

                            {selectedFeedback.adminNotes && (
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem', mb: 0.5 }}>
                                        Admin Notes
                                    </Typography>
                                    <Paper sx={{ p: 2, background: theme.colors.bg.tertiary, border: `1px solid ${theme.colors.border.primary}` }}>
                                        <Typography sx={{ color: theme.colors.text.secondary }}>
                                            {selectedFeedback.adminNotes}
                                        </Typography>
                                    </Paper>
                                </Box>
                            )}

                            {selectedFeedback.suggestedResponse && (
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem', mb: 0.5 }}>
                                        Suggested Response
                                    </Typography>
                                    <Paper sx={{ p: 2, background: theme.colors.status.success + '10', border: `1px solid ${theme.colors.status.success}30` }}>
                                        <Typography sx={{ color: theme.colors.text.secondary }}>
                                            {selectedFeedback.suggestedResponse}
                                        </Typography>
                                    </Paper>
                                </Box>
                            )}

                            {selectedFeedback.resolutionNotes && (
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem', mb: 0.5 }}>
                                        Resolution Notes
                                    </Typography>
                                    <Paper sx={{ p: 2, background: theme.colors.bg.tertiary, border: `1px solid ${theme.colors.border.primary}` }}>
                                        <Typography sx={{ color: theme.colors.text.secondary }}>
                                            {selectedFeedback.resolutionNotes}
                                        </Typography>
                                    </Paper>
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                                        Created
                                    </Typography>
                                    <Typography sx={{ color: theme.colors.text.secondary }}>
                                        {formatDate(selectedFeedback.createdAt)}
                                    </Typography>
                                </Box>
                                {selectedFeedback.resolvedAt && (
                                    <Box>
                                        <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                                            Resolved
                                        </Typography>
                                        <Typography sx={{ color: theme.colors.text.secondary }}>
                                            {formatDate(selectedFeedback.resolvedAt)}
                                        </Typography>
                                    </Box>
                                )}
                                {selectedFeedback.reviewedBy && (
                                    <Box>
                                        <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                                            Reviewed By
                                        </Typography>
                                        <Typography sx={{ color: theme.colors.text.secondary }}>
                                            {selectedFeedback.reviewedBy}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ borderTop: `1px solid ${theme.colors.border.primary}`, p: 2 }}>
                    <Button onClick={() => setViewModalOpen(false)} sx={{ color: theme.colors.text.secondary }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Export Modal */}
            <Dialog
                open={exportModalOpen}
                onClose={() => setExportModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        background: theme.colors.bg.secondary,
                        border: `1px solid ${theme.colors.border.primary}`
                    }
                }}
            >
                <DialogTitle sx={{ color: theme.colors.text.primary, borderBottom: `1px solid ${theme.colors.border.primary}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DownloadIcon sx={{ color: theme.colors.status.info }} />
                        Export Training Data
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Alert severity="info" sx={{ mb: 3, background: theme.colors.status.info + '20', color: theme.colors.text.primary }}>
                        Export feedback data for fine-tuning or reinforcement learning. JSONL format is compatible with most ML training pipelines.
                    </Alert>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <FormControl fullWidth>
                            <InputLabel sx={{ color: theme.colors.text.quaternary }}>Export Format</InputLabel>
                            <Select
                                value={exportFormat}
                                label="Export Format"
                                onChange={(e) => setExportFormat(e.target.value)}
                                sx={{
                                    background: theme.colors.bg.tertiary,
                                    color: theme.colors.text.primary,
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.colors.border.primary }
                                }}
                            >
                                <MenuItem value="jsonl">JSONL (Fine-tuning)</MenuItem>
                                <MenuItem value="rlhf_pairs">RLHF Preference Pairs</MenuItem>
                            </Select>
                        </FormControl>

                        <Box>
                            <Typography sx={{ color: theme.colors.text.secondary, mb: 1.5, fontWeight: 500 }}>
                                Include Feedback Types:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 3 }}>
                                <Box
                                    onClick={() => setIncludeGood(!includeGood)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        cursor: 'pointer',
                                        p: 1.5,
                                        borderRadius: theme.borderRadius.sm,
                                        background: includeGood ? theme.colors.status.success + '20' : theme.colors.bg.tertiary,
                                        border: `1px solid ${includeGood ? theme.colors.status.success : theme.colors.border.primary}`,
                                        transition: 'all 0.2s ease',
                                        '&:hover': { borderColor: theme.colors.status.success }
                                    }}
                                >
                                    <Box sx={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: '4px',
                                        border: `2px solid ${includeGood ? theme.colors.status.success : theme.colors.text.quaternary}`,
                                        background: includeGood ? theme.colors.status.success : 'transparent',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        {includeGood && <CheckCircleIcon sx={{ fontSize: 14, color: 'white' }} />}
                                    </Box>
                                    <ThumbUpIcon sx={{ color: theme.colors.status.success, fontSize: 18 }} />
                                    <Typography sx={{ color: theme.colors.text.secondary }}>Good Feedback</Typography>
                                </Box>
                                <Box
                                    onClick={() => setIncludeBad(!includeBad)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        cursor: 'pointer',
                                        p: 1.5,
                                        borderRadius: theme.borderRadius.sm,
                                        background: includeBad ? theme.colors.status.error + '20' : theme.colors.bg.tertiary,
                                        border: `1px solid ${includeBad ? theme.colors.status.error : theme.colors.border.primary}`,
                                        transition: 'all 0.2s ease',
                                        '&:hover': { borderColor: theme.colors.status.error }
                                    }}
                                >
                                    <Box sx={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: '4px',
                                        border: `2px solid ${includeBad ? theme.colors.status.error : theme.colors.text.quaternary}`,
                                        background: includeBad ? theme.colors.status.error : 'transparent',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        {includeBad && <CheckCircleIcon sx={{ fontSize: 14, color: 'white' }} />}
                                    </Box>
                                    <ThumbDownIcon sx={{ color: theme.colors.status.error, fontSize: 18 }} />
                                    <Typography sx={{ color: theme.colors.text.secondary }}>Bad Feedback</Typography>
                                </Box>
                            </Box>
                            {!includeGood && !includeBad && (
                                <Typography sx={{ color: theme.colors.status.warning, fontSize: '0.8rem', mt: 1 }}>
                                    Select at least one feedback type to export
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ borderTop: `1px solid ${theme.colors.border.primary}`, p: 2 }}>
                    <Button onClick={() => setExportModalOpen(false)} sx={{ color: theme.colors.text.secondary }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleExport}
                        disabled={exporting || (!includeGood && !includeBad)}
                        variant="contained"
                        startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
                        sx={{
                            background: theme.colors.status.info,
                            '&:hover': { background: '#2563eb' },
                            '&.Mui-disabled': { background: theme.colors.bg.tertiary, color: theme.colors.text.quaternary }
                        }}
                    >
                        {exporting ? 'Exporting...' : 'Export'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Resolve Feedback Modal */}
            <Dialog
                open={resolveModalOpen}
                onClose={() => setResolveModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        background: theme.colors.bg.secondary,
                        border: `1px solid ${theme.colors.border.primary}`
                    }
                }}
            >
                <DialogTitle sx={{ color: theme.colors.text.primary, borderBottom: `1px solid ${theme.colors.border.primary}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ color: theme.colors.status.success }} />
                        Mark Feedback as Resolved
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    {selectedFeedback && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ p: 2, background: theme.colors.bg.tertiary, borderRadius: theme.borderRadius.sm, border: `1px solid ${theme.colors.border.primary}` }}>
                                <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem', mb: 0.5 }}>
                                    Issue Type
                                </Typography>
                                <Chip
                                    label={selectedFeedback.issueType?.replace(/_/g, ' ') || 'General'}
                                    size="small"
                                    sx={{
                                        background: theme.colors.status.warning + '20',
                                        color: theme.colors.status.warning,
                                        textTransform: 'capitalize',
                                        mb: 1.5
                                    }}
                                />
                                <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem', mb: 0.5 }}>
                                    User Message
                                </Typography>
                                <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.9rem' }}>
                                    {truncateText(selectedFeedback.userMessage, 150)}
                                </Typography>
                            </Box>

                            <TextField
                                label="Resolution Notes"
                                multiline
                                rows={4}
                                fullWidth
                                value={resolutionNotes}
                                onChange={(e) => setResolutionNotes(e.target.value)}
                                placeholder="Describe how the issue was addressed or why it was resolved..."
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        background: theme.colors.bg.tertiary,
                                        '& fieldset': { borderColor: theme.colors.border.primary }
                                    },
                                    '& .MuiInputBase-input': { color: theme.colors.text.primary },
                                    '& .MuiInputLabel-root': { color: theme.colors.text.quaternary }
                                }}
                            />

                            <Alert severity="info" sx={{ background: theme.colors.status.info + '15', color: theme.colors.text.secondary }}>
                                Resolving feedback marks it as reviewed. The original feedback will be preserved for training purposes.
                            </Alert>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ borderTop: `1px solid ${theme.colors.border.primary}`, p: 2, gap: 1 }}>
                    <Button onClick={() => setResolveModalOpen(false)} sx={{ color: theme.colors.text.secondary }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleResolveFeedback}
                        disabled={resolving}
                        variant="contained"
                        startIcon={resolving ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                        sx={{
                            background: theme.colors.status.success,
                            '&:hover': { background: '#16a34a' }
                        }}
                    >
                        {resolving ? 'Resolving...' : 'Mark as Resolved'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Sync to Prompts Modal */}
            <Dialog
                open={syncModalOpen}
                onClose={() => setSyncModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        background: theme.colors.bg.secondary,
                        border: `1px solid ${theme.colors.border.primary}`
                    }
                }}
            >
                <DialogTitle sx={{ color: theme.colors.text.primary, borderBottom: `1px solid ${theme.colors.border.primary}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SyncIcon sx={{ color: theme.colors.accent.tertiary }} />
                        Sync Feedback to Prompts
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Alert severity="warning" sx={{ mb: 3, background: theme.colors.status.warning + '20', color: theme.colors.text.primary }}>
                        This will add user feedback as good/bad examples to the reinforcement learning prompt in the database. Use "Preview" first to see what will be changed.
                    </Alert>

                    <Box sx={{ mb: 3 }}>
                        <Typography sx={{ color: theme.colors.text.secondary, mb: 2 }}>
                            Sync will:
                        </Typography>
                        <Box sx={{ pl: 2 }}>
                            <Typography sx={{ color: theme.colors.text.tertiary, fontSize: '0.9rem', mb: 1 }}>
                                • Add <strong style={{ color: theme.colors.status.success }}>good feedback</strong> as positive examples
                            </Typography>
                            <Typography sx={{ color: theme.colors.text.tertiary, fontSize: '0.9rem', mb: 1 }}>
                                • Add <strong style={{ color: theme.colors.status.error }}>bad feedback</strong> as negative examples
                            </Typography>
                            <Typography sx={{ color: theme.colors.text.tertiary, fontSize: '0.9rem' }}>
                                • Update the <code style={{ background: theme.colors.bg.tertiary, padding: '2px 6px', borderRadius: 4 }}>reinforcement_learning</code> prompt
                            </Typography>
                        </Box>
                    </Box>

                    {/* Sync History */}
                    <Divider sx={{ borderColor: theme.colors.border.primary, my: 2 }} />
                    <Typography sx={{ color: theme.colors.text.secondary, fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HistoryIcon sx={{ fontSize: 18 }} />
                        Recent Sync History
                    </Typography>
                    {syncHistory.length > 0 ? (
                        <Box sx={{ maxHeight: 150, overflow: 'auto' }}>
                            {syncHistory.map((entry, index) => (
                                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: `1px solid ${theme.colors.border.secondary}` }}>
                                    <Typography sx={{ color: theme.colors.text.tertiary, fontSize: '0.85rem' }}>
                                        {formatDate(entry.syncDate)}
                                    </Typography>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.85rem' }}>
                                        +{entry.goodAdded} good, +{entry.badAdded} bad
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.85rem' }}>
                            No sync history available
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ borderTop: `1px solid ${theme.colors.border.primary}`, p: 2, gap: 1 }}>
                    <Button onClick={() => setSyncModalOpen(false)} sx={{ color: theme.colors.text.secondary }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => handleSync(true)}
                        disabled={syncing}
                        variant="outlined"
                        sx={{
                            borderColor: theme.colors.border.primary,
                            color: theme.colors.text.secondary,
                            '&:hover': { borderColor: theme.colors.accent.primary }
                        }}
                    >
                        Preview
                    </Button>
                    <Button
                        onClick={() => handleSync(false)}
                        disabled={syncing}
                        variant="contained"
                        startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
                        sx={{
                            background: theme.gradients.primaryAccent,
                            '&:hover': { background: theme.colors.accent.secondary }
                        }}
                    >
                        {syncing ? 'Syncing...' : 'Sync Now'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
});

AdminFeedbackDashboard.displayName = 'AdminFeedbackDashboard';

export default AdminFeedbackDashboard;
